// components/LoadTrackingMap.js
import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { useSelector } from 'react-redux';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { selectTrackingHistoryByLoadId } from '../store/slices/trackingHistorySlice';
import { formatDateTime } from '../utils/formatters';

// Map layer configurations - URLs are generated dynamically to ensure env var is loaded
const getMapLayers = () => {
  const apiKey = process.env.REACT_APP_MAPTILER_API_KEY;
  
  if (!apiKey) {
    console.warn('MapTiler API key not found. Satellite/Hybrid views may not work. Check REACT_APP_MAPTILER_API_KEY in .env');
  }
  
  return {
    street: {
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: '&copy; OpenStreetMap contributors',
      name: 'Street'
    },
    satellite: {
      url: `https://api.maptiler.com/maps/satellite/{z}/{x}/{y}.jpg?key=${apiKey || ''}`,
      attribution: '&copy; <a href="https://www.maptiler.com/copyright/" target="_blank">MapTiler</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      name: 'Satellite'
    },
    hybrid: {
      url: `https://api.maptiler.com/maps/hybrid/{z}/{x}/{y}.jpg?key=${apiKey || ''}`,
      attribution: '&copy; <a href="https://www.maptiler.com/copyright/" target="_blank">MapTiler</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      name: 'Hybrid'
    }
  };
};

// Calculate bearing between two points (returns degrees 0-360)
const calculateBearing = (lat1, lon1, lat2, lon2) => {
  const toRad = (deg) => deg * Math.PI / 180;
  const toDeg = (rad) => rad * 180 / Math.PI;
  
  const dLon = toRad(lon2 - lon1);
  const y = Math.sin(dLon) * Math.cos(toRad(lat2));
  const x = Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) - 
            Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLon);
  
  let bearing = toDeg(Math.atan2(y, x));
  return (bearing + 360) % 360;
};

// Create arrow icon with rotation - sleek chevron design
const createArrowIcon = (bearing, isCompleted = true, isSelected = false, size = 'normal') => {
  const sizeConfig = {
    small: { width: 14, height: 14, stroke: 2 },
    normal: { width: 18, height: 18, stroke: 2.5 },
    large: { width: 28, height: 28, stroke: 3 }
  };
  
  const { width, height, stroke } = sizeConfig[size];
  
  // Color based on state
  let color, fillOpacity, bgColor;
  if (isSelected) {
    color = '#7C3AED'; // violet-600
    fillOpacity = 1;
    bgColor = 'rgba(124, 58, 237, 0.2)';
  } else if (isCompleted) {
    color = '#3B82F6'; // blue-500
    fillOpacity = 0.9;
    bgColor = 'rgba(59, 130, 246, 0.15)';
  } else {
    color = '#9CA3AF'; // gray-400
    fillOpacity = 0.6;
    bgColor = 'rgba(156, 163, 175, 0.1)';
  }
  
  // SVG arrow pointing up (will be rotated)
  const svg = `
    <svg width="${width}" height="${height}" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" 
         style="transform: rotate(${bearing}deg); filter: drop-shadow(0 1px 2px rgba(0,0,0,0.2));">
      <circle cx="12" cy="12" r="10" fill="${bgColor}" stroke="${color}" stroke-width="1.5" opacity="${fillOpacity}"/>
      <path d="M12 6 L17 14 L12 11 L7 14 Z" fill="${color}" stroke="${color}" stroke-width="${stroke}" 
            stroke-linejoin="round" stroke-linecap="round"/>
    </svg>
  `;
  
  return L.divIcon({
    html: svg,
    className: 'arrow-marker',
    iconSize: [width, height],
    iconAnchor: [width / 2, height / 2],
    popupAnchor: [0, -height / 2]
  });
};

// Create selected point marker - pulse ring effect
const createSelectedPointIcon = () => {
  const svg = `
    <div style="position: relative; width: 32px; height: 32px;">
      <div style="
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 32px;
        height: 32px;
        background: rgba(124, 58, 237, 0.3);
        border-radius: 50%;
        animation: pulse-ring 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
      "></div>
      <div style="
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 16px;
        height: 16px;
        background: linear-gradient(135deg, #8B5CF6, #7C3AED);
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(124, 58, 237, 0.5);
      "></div>
    </div>
    <style>
      @keyframes pulse-ring {
        0% { transform: translate(-50%, -50%) scale(0.8); opacity: 1; }
        100% { transform: translate(-50%, -50%) scale(1.4); opacity: 0; }
      }
    </style>
  `;
  
  return L.divIcon({
    html: svg,
    className: 'selected-point-marker',
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16]
  });
};

// Create vehicle marker with heading direction and speed state
// Shows directional arrow when moving, parked indicator when stationary
const createVehicleIcon = (heading = 0, speed = 0, source = 'app') => {
  const isParked = speed < 2; // Consider parked if speed < 2 mph
  const isMoving = speed >= 2;
  const isFast = speed >= 55; // Highway speed
  
  // Color scheme based on state and source
  const sourceColor = source === 'samsara' ? '#10B981' : '#3B82F6'; // emerald for Samsara, blue for app
  const movingColor = isFast ? '#F59E0B' : sourceColor; // amber for highway speed
  const parkedColor = '#6B7280'; // gray-500
  
  const mainColor = isParked ? parkedColor : movingColor;
  const glowColor = isParked ? 'rgba(107, 114, 128, 0.3)' : (isFast ? 'rgba(245, 158, 11, 0.4)' : `${sourceColor}40`);
  
  const size = 44;
  const innerSize = 32;
  
  // Create the SVG with proper rotation and styling
  const svg = isParked ? `
    <!-- Parked Vehicle Marker -->
    <div style="position: relative; width: ${size}px; height: ${size}px;">
      <!-- Outer glow ring -->
      <div style="
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: ${size}px;
        height: ${size}px;
        background: ${glowColor};
        border-radius: 50%;
        animation: parked-pulse 3s ease-in-out infinite;
      "></div>
      <!-- Main circle -->
      <div style="
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: ${innerSize}px;
        height: ${innerSize}px;
        background: linear-gradient(145deg, #4B5563, #374151);
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 4px 12px rgba(0,0,0,0.25);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <!-- P indicator for parked -->
        <span style="
          color: white;
          font-size: 14px;
          font-weight: 700;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          text-shadow: 0 1px 2px rgba(0,0,0,0.3);
        ">P</span>
      </div>
      <!-- Small status dot -->
      <div style="
        position: absolute;
        top: 4px;
        right: 4px;
        width: 10px;
        height: 10px;
        background: ${parkedColor};
        border: 2px solid white;
        border-radius: 50%;
        box-shadow: 0 1px 3px rgba(0,0,0,0.2);
      "></div>
    </div>
    <style>
      @keyframes parked-pulse {
        0%, 100% { opacity: 0.6; transform: translate(-50%, -50%) scale(1); }
        50% { opacity: 0.3; transform: translate(-50%, -50%) scale(1.1); }
      }
    </style>
  ` : `
    <!-- Moving Vehicle Marker -->
    <div style="position: relative; width: ${size}px; height: ${size}px;">
      <!-- Motion trail effect (only for moving vehicles) -->
      ${isMoving ? `
        <div style="
          position: absolute;
          top: 50%;
          left: 50%;
          width: ${size + 8}px;
          height: ${size + 8}px;
          transform: translate(-50%, -50%) rotate(${heading}deg);
          pointer-events: none;
        ">
          <!-- Motion lines behind the vehicle -->
          <div style="
            position: absolute;
            bottom: -2px;
            left: 50%;
            transform: translateX(-50%);
            width: 3px;
            height: 12px;
            background: linear-gradient(to top, transparent, ${mainColor}40);
            border-radius: 2px;
          "></div>
          <div style="
            position: absolute;
            bottom: 0px;
            left: calc(50% - 6px);
            transform: translateX(-50%);
            width: 2px;
            height: 8px;
            background: linear-gradient(to top, transparent, ${mainColor}30);
            border-radius: 2px;
          "></div>
          <div style="
            position: absolute;
            bottom: 0px;
            left: calc(50% + 6px);
            transform: translateX(-50%);
            width: 2px;
            height: 8px;
            background: linear-gradient(to top, transparent, ${mainColor}30);
            border-radius: 2px;
          "></div>
        </div>
      ` : ''}
      
      <!-- Animated glow ring for moving vehicles -->
      <div style="
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: ${size}px;
        height: ${size}px;
        background: ${glowColor};
        border-radius: 50%;
        animation: ${isFast ? 'fast-pulse' : 'moving-pulse'} ${isFast ? '0.8s' : '2s'} ease-in-out infinite;
      "></div>
      
      <!-- Main directional arrow container -->
      <div style="
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: ${innerSize}px;
        height: ${innerSize}px;
      ">
        <svg 
          width="${innerSize}" 
          height="${innerSize}" 
          viewBox="0 0 32 32" 
          style="
            transform: rotate(${heading}deg);
            filter: drop-shadow(0 3px 6px rgba(0,0,0,0.25));
          "
        >
          <!-- Outer circle with gradient -->
          <defs>
            <linearGradient id="vehicleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style="stop-color:${mainColor};stop-opacity:1" />
              <stop offset="100%" style="stop-color:${mainColor};stop-opacity:0.8" />
            </linearGradient>
          </defs>
          <circle cx="16" cy="16" r="14" fill="url(#vehicleGradient)" stroke="white" stroke-width="2.5"/>
          <!-- Arrow pointing up (north) - will be rotated by heading -->
          <path 
            d="M16 7 L22 18 L16 14 L10 18 Z" 
            fill="white" 
            stroke="white" 
            stroke-width="0.5" 
            stroke-linejoin="round"
          />
        </svg>
      </div>
      
      <!-- Speed badge -->
      <div style="
        position: absolute;
        bottom: -2px;
        right: -2px;
        min-width: 22px;
        height: 16px;
        padding: 0 4px;
        background: ${isFast ? '#F59E0B' : mainColor};
        border: 2px solid white;
        border-radius: 8px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <span style="
          color: white;
          font-size: 9px;
          font-weight: 700;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          line-height: 1;
        ">${Math.round(speed)}</span>
      </div>
    </div>
    <style>
      @keyframes moving-pulse {
        0%, 100% { opacity: 0.8; transform: translate(-50%, -50%) scale(1); }
        50% { opacity: 0.4; transform: translate(-50%, -50%) scale(1.15); }
      }
      @keyframes fast-pulse {
        0%, 100% { opacity: 0.9; transform: translate(-50%, -50%) scale(1); }
        50% { opacity: 0.5; transform: translate(-50%, -50%) scale(1.2); }
      }
    </style>
  `;
  
  return L.divIcon({
    html: svg,
    className: 'vehicle-marker',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2]
  });
};

const MapUpdater = ({ bounds }) => {
  const map = useMap();
  const hasInitializedRef = React.useRef(false);
  
  useEffect(() => {
    // Short delay to allow container resize to complete
    const timer = setTimeout(() => {
      map.invalidateSize();
      // Only fit bounds on initial load, not on subsequent updates
      // This prevents the map from zooming out when telematics data updates
      if (bounds && !hasInitializedRef.current) {
        map.fitBounds(bounds, { padding: [50, 50] });
        hasInitializedRef.current = true;
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [map, bounds]);

  return null;
};

// Layer Toggle Control Component - Sleek floating button design
const LayerToggleControl = ({ activeLayer, onLayerChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const layers = [
    { id: 'street', icon: '🗺️', label: 'Street' },
    { id: 'satellite', icon: '🛰️', label: 'Satellite' },
    { id: 'hybrid', icon: '🌍', label: 'Hybrid' }
  ];
  
  const activeLayerData = layers.find(l => l.id === activeLayer);
  
  return (
    <div className="absolute top-4 right-4 z-[1000]">
      <div 
        className={`
          bg-white/95 backdrop-blur-md rounded-xl shadow-lg border border-gray-200/50
          transition-all duration-300 ease-out overflow-hidden
          ${isExpanded ? 'w-40' : 'w-auto'}
        `}
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
      >
        {/* Collapsed state - shows current layer */}
        <div 
          className={`
            flex items-center gap-2 px-3 py-2.5 cursor-pointer
            ${isExpanded ? 'border-b border-gray-100' : ''}
          `}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center shadow-sm">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
          </div>
          <div className={`flex flex-col transition-opacity duration-200 ${isExpanded ? 'opacity-100' : 'opacity-0 w-0'}`}>
            <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">Map Style</span>
            <span className="text-xs font-semibold text-gray-700">{activeLayerData?.label}</span>
          </div>
          {isExpanded && (
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-gray-400 ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </div>
        
        {/* Expanded layer options */}
        <div className={`
          transition-all duration-300 ease-out
          ${isExpanded ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}
        `}>
          {layers.map((layer) => (
            <button
              key={layer.id}
              onClick={() => {
                onLayerChange(layer.id);
                setIsExpanded(false);
              }}
              className={`
                w-full flex items-center gap-3 px-3 py-2 text-left transition-all duration-150
                ${activeLayer === layer.id 
                  ? 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700' 
                  : 'hover:bg-gray-50 text-gray-600 hover:text-gray-900'
                }
              `}
            >
              <span className="text-base">{layer.icon}</span>
              <span className={`text-sm ${activeLayer === layer.id ? 'font-semibold' : 'font-medium'}`}>
                {layer.label}
              </span>
              {activeLayer === layer.id && (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 ml-auto text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const LoadTrackingMap = ({ 
  load, 
  telematics, 
  selectedTimelineIndex = -1,
  showBreadcrumbs = false 
}) => {
  const [activeLayer, setActiveLayer] = useState('street');
  
  // Get map layers configuration (memoized)
  const MAP_LAYERS = useMemo(() => getMapLayers(), []);
  
  const trackingHistory = useSelector(
    state => selectTrackingHistoryByLoadId(state, load?.id)
  );

  // Memoize marker icons to prevent recreating them on every render
  const markers = useMemo(() => ({
    origin: L.icon({
      iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    }),
    destination: L.icon({
      iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    }),
    truck: L.icon({
      iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    }),
    historyCompleted: L.icon({
      iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
      iconSize: [18, 29], // Smaller than other markers
      iconAnchor: [9, 29],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    }),
    historyPending: L.icon({
      iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-grey.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
      iconSize: [16, 26], // Smaller than completed markers
      iconAnchor: [8, 26],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    }),
    historySelected: L.icon({
      iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-violet.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
      iconSize: [30, 49], // Larger than other markers
      iconAnchor: [15, 49],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    })
  }), []);

  // Calculate bounds and path points with breadcrumb support
  const { bounds, pathPoints, completedPath, pendingPath } = useMemo(() => {
    if (!load) return { bounds: null, pathPoints: [], completedPath: [], pendingPath: [] };
    
    const points = [];
    const allPath = [];
    const completedPathPoints = [];
    const pendingPathPoints = [];
    
    // Add origin point
    if (load.origin_latitude != null && load.origin_longitude != null) {
      const originPoint = [parseFloat(load.origin_latitude), parseFloat(load.origin_longitude)];
      points.push(originPoint);
      allPath.push(originPoint);
      completedPathPoints.push(originPoint);
    }
    
    // Sort tracking history by created_at to ensure chronological order for line rendering
    const sortedHistory = [...trackingHistory].sort((a, b) => 
      new Date(a.created_at) - new Date(b.created_at)
    );
    
    // Add historical points with breadcrumbs logic
    if (sortedHistory.length > 0) {
      sortedHistory.forEach((point, index) => {
        if (point.latitude != null && point.longitude != null) {
          const historyPoint = [parseFloat(point.latitude), parseFloat(point.longitude)];
          points.push(historyPoint);
          allPath.push(historyPoint);
          
          // Add to completed or pending path based on timeline selection
          if (showBreadcrumbs && selectedTimelineIndex >= 0) {
            if (index <= selectedTimelineIndex) {
              completedPathPoints.push(historyPoint);
            } else {
              // Include the last completed point as the first pending point for continuity
              if (pendingPathPoints.length === 0 && completedPathPoints.length > 0) {
                pendingPathPoints.push(completedPathPoints[completedPathPoints.length - 1]);
              }
              pendingPathPoints.push(historyPoint);
            }
          } else {
            completedPathPoints.push(historyPoint);
          }
        }
      });
    }
    
    // Add current position if available
    if (telematics?.location?.latitude != null && telematics?.location?.longitude != null) {
      const currentPoint = [
        parseFloat(telematics.location.latitude),
        parseFloat(telematics.location.longitude)
      ];
      points.push(currentPoint);
      allPath.push(currentPoint);
      
      if (!showBreadcrumbs || selectedTimelineIndex === -1) {
        completedPathPoints.push(currentPoint);
      } else {
        // Add to pending path when in breadcrumbs mode
        if (pendingPathPoints.length === 0 && completedPathPoints.length > 0) {
          pendingPathPoints.push(completedPathPoints[completedPathPoints.length - 1]);
        }
        pendingPathPoints.push(currentPoint);
      }
    }
    
    // Add destination point - only for reference bounds, not for path
    if (load.destination_latitude != null && load.destination_longitude != null) {
      const destPoint = [
        parseFloat(load.destination_latitude),
        parseFloat(load.destination_longitude)
      ];
      points.push(destPoint);
    }

    return {
      bounds: points.length > 0 ? L.latLngBounds(points) : null,
      pathPoints: allPath,
      completedPath: completedPathPoints,
      pendingPath: pendingPathPoints
    };
  }, [load, telematics, trackingHistory, selectedTimelineIndex, showBreadcrumbs]);

  // Get visible history points based on timeline selection
  const visibleHistoryPoints = useMemo(() => {
    if (!showBreadcrumbs || selectedTimelineIndex < 0) {
      return trackingHistory;
    }
    return trackingHistory.slice(0, selectedTimelineIndex + 1);
  }, [trackingHistory, selectedTimelineIndex, showBreadcrumbs]);

  // Calculate journey arrows for breadcrumb mode - evenly distributed with bearings
  const journeyArrows = useMemo(() => {
    if (!showBreadcrumbs || !trackingHistory.length) return [];
    
    // Sort tracking history by created_at to ensure chronological order
    const sortedHistory = [...trackingHistory].sort((a, b) => 
      new Date(a.created_at) - new Date(b.created_at)
    );
    
    const arrows = [];
    const totalPoints = sortedHistory.length;
    
    // Determine how many arrows to show based on total points
    // Show more arrows for shorter journeys, fewer for longer ones
    let interval = 1;
    if (totalPoints > 50) interval = 5;
    else if (totalPoints > 30) interval = 3;
    else if (totalPoints > 15) interval = 2;
    
    sortedHistory.forEach((point, index) => {
      if (point.latitude == null || point.longitude == null) return;
      
      // Always include first point, last point, selected point, and interval points
      const isSelected = index === selectedTimelineIndex;
      const isFirstOrLast = index === 0 || index === totalPoints - 1;
      const isIntervalPoint = index % interval === 0;
      
      if (!isSelected && !isFirstOrLast && !isIntervalPoint) return;
      
      // Calculate bearing to next point (or from previous if last point)
      let bearing = 0;
      if (index < totalPoints - 1) {
        const nextPoint = sortedHistory[index + 1];
        if (nextPoint?.latitude != null && nextPoint?.longitude != null) {
          bearing = calculateBearing(
            point.latitude, point.longitude,
            nextPoint.latitude, nextPoint.longitude
          );
        }
      } else if (index > 0) {
        const prevPoint = sortedHistory[index - 1];
        if (prevPoint?.latitude != null && prevPoint?.longitude != null) {
          bearing = calculateBearing(
            prevPoint.latitude, prevPoint.longitude,
            point.latitude, point.longitude
          );
        }
      }
      
      arrows.push({
        position: [point.latitude, point.longitude],
        bearing,
        index,
        isCompleted: selectedTimelineIndex < 0 || index <= selectedTimelineIndex,
        isSelected,
        point,
        size: isSelected ? 'large' : (isFirstOrLast ? 'normal' : 'small')
      });
    });
    
    return arrows;
  }, [trackingHistory, selectedTimelineIndex, showBreadcrumbs]);

  // Default center of USA if no bounds
  const defaultCenter = [39.8283, -98.5795];
  const defaultZoom = 4;

  // Enhanced historical markers with breadcrumb states
  const renderHistoricalMarkers = () => {
    if (!trackingHistory.length) return null;

    // In journey mode (showBreadcrumbs), use directional arrow markers
    if (showBreadcrumbs) {
      return (
        <>
          {journeyArrows.map((arrow) => {
            // Use special selected point icon for the selected marker
            const icon = arrow.isSelected 
              ? createSelectedPointIcon()
              : createArrowIcon(arrow.bearing, arrow.isCompleted, false, arrow.size);
            
            return (
              <Marker
                key={`journey-arrow-${arrow.index}`}
                position={arrow.position}
                icon={icon}
                zIndexOffset={arrow.isSelected ? 1000 : (arrow.isCompleted ? 200 : 100)}
              >
                <Popup>
                  <div className="p-3 min-w-[200px]">
                    <h3 className={`font-semibold mb-2 ${
                      arrow.isSelected ? 'text-violet-600' : 
                      arrow.isCompleted ? 'text-blue-600' : 'text-gray-500'
                    }`}>
                      {arrow.isSelected ? '📍 Current Selection' : 
                       arrow.isCompleted ? 'Completed Waypoint' : 'Upcoming Waypoint'}
                    </h3>
                    <div className="space-y-1 text-sm">
                      <p>
                        <span className="font-medium">Point:</span> {arrow.index + 1} of {trackingHistory.length}
                      </p>
                      <p>
                        <span className="font-medium">Time:</span>{' '}
                        {formatDateTime(arrow.point.created_at)}
                      </p>
                      <p>
                        <span className="font-medium">Position:</span>{' '}
                        {arrow.point.latitude.toFixed(4)}, {arrow.point.longitude.toFixed(4)}
                      </p>
                      {arrow.point.speed != null && (
                        <p>
                          <span className="font-medium">Speed:</span> {arrow.point.speed} mph
                        </p>
                      )}
                      <p>
                        <span className="font-medium">Heading:</span> {Math.round(arrow.bearing)}°
                      </p>
                      {arrow.isSelected && (
                        <div className="mt-2 p-2 bg-violet-50 rounded border border-violet-200">
                          <p className="text-xs text-violet-600 font-medium">
                            Currently viewing this position
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </>
      );
    }

    // Standard mode - use clustered markers
    return (
      <>
        {/* Completed/Viewed tracking points */}
        {visibleHistoryPoints.length > 0 && (
          <MarkerClusterGroup 
            chunkedLoading
            maxClusterRadius={30}
            spiderfyDistanceMultiplier={2}
          >
            {visibleHistoryPoints.map((point, index) => {
              const isSelected = index === selectedTimelineIndex;
              const icon = isSelected ? markers.historySelected : markers.historyCompleted;
              
              return (
                <Marker
                  key={`history-completed-${index}`}
                  position={[point.latitude, point.longitude]}
                  icon={icon}
                  zIndexOffset={isSelected ? 1000 : 100}
                >
                  <Popup>
                    <div className="p-3 min-w-[200px]">
                      <h3 className="font-semibold text-blue-600 mb-2">
                        {isSelected ? 'Selected Point' : 'Historical Location'}
                      </h3>
                      <div className="space-y-1 text-sm">
                        <p>
                          <span className="font-medium">Time:</span> {' '}
                          {formatDateTime(point.created_at)}
                        </p>
                        <p>
                          <span className="font-medium">Position:</span> {' '}
                          {point.latitude.toFixed(4)}, {point.longitude.toFixed(4)}
                        </p>
                        {point.speed != null && (
                          <p>
                            <span className="font-medium">Speed:</span> {point.speed} mph
                          </p>
                        )}
                        {point.notes && (
                          <div className="mt-2 p-2 bg-gray-50 rounded">
                            <p className="text-xs text-gray-600">{point.notes}</p>
                          </div>
                        )}
                        {isSelected && (
                          <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                            <p className="text-xs text-blue-600 font-medium">
                              Timeline Position: {index + 1} of {trackingHistory.length}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MarkerClusterGroup>
        )}
      </>
    );
  };

  if (!load) return null;

  return (
    <div className="relative z-0 h-full">
      <MapContainer
        bounds={bounds}
        center={!bounds ? defaultCenter : undefined}
        zoom={!bounds ? defaultZoom : undefined}
        className="h-full w-full rounded-lg z-0"
        zoomControl={true}
      >
        <MapUpdater bounds={bounds} />
        <TileLayer
          key={activeLayer}
          url={MAP_LAYERS[activeLayer].url}
          attribution={MAP_LAYERS[activeLayer].attribution}
          maxZoom={activeLayer === 'street' ? 19 : 20}
          tileSize={activeLayer === 'street' ? 256 : 512}
          zoomOffset={activeLayer === 'street' ? 0 : -1}
        />

        {/* Enhanced path visualization with breadcrumbs */}
        {showBreadcrumbs ? (
          <>
            {/* Completed path - gradient-like with shadow effect */}
            {completedPath.length > 1 && (
              <>
                {/* Shadow/glow layer */}
                <Polyline
                  positions={completedPath}
                  color="#3B82F6"
                  weight={8}
                  opacity={0.15}
                  lineCap="round"
                  lineJoin="round"
                />
                {/* Main line */}
                <Polyline
                  positions={completedPath}
                  color="#3B82F6"
                  weight={4}
                  opacity={0.85}
                  lineCap="round"
                  lineJoin="round"
                />
                {/* Highlight center line */}
                <Polyline
                  positions={completedPath}
                  color="#60A5FA"
                  weight={2}
                  opacity={0.6}
                  lineCap="round"
                  lineJoin="round"
                />
              </>
            )}
            
            {/* Pending path - dashed with subtle styling */}
            {pendingPath.length > 1 && (
              <>
                {/* Shadow layer */}
                <Polyline
                  positions={pendingPath}
                  color="#9CA3AF"
                  weight={4}
                  opacity={0.1}
                  lineCap="round"
                  lineJoin="round"
                />
                {/* Dashed line */}
                <Polyline
                  positions={pendingPath}
                  color="#9CA3AF"
                  weight={2}
                  opacity={0.4}
                  dashArray="8, 12"
                  lineCap="round"
                  lineJoin="round"
                />
              </>
            )}
          </>
        ) : (
          /* Standard path - single blue line with subtle styling */
          pathPoints.length > 1 && (
            <>
              <Polyline
                positions={pathPoints}
                color="#3B82F6"
                weight={5}
                opacity={0.2}
                lineCap="round"
                lineJoin="round"
              />
              <Polyline
                positions={pathPoints}
                color="#3B82F6"
                weight={3}
                opacity={0.7}
                lineCap="round"
                lineJoin="round"
              />
            </>
          )
        )}

        {/* Enhanced historical markers */}
        {renderHistoricalMarkers()}

        {/* Key location markers */}
        {load.origin_latitude && load.origin_longitude && (
          <Marker
            position={[load.origin_latitude, load.origin_longitude]}
            icon={markers.origin}
            zIndexOffset={500}
          >
            <Popup>
              <div className="p-3">
                <h3 className="font-semibold text-green-600 mb-2">Pickup Location</h3>
                <div className="space-y-1 text-sm">
                  <p><span className="font-medium">City:</span> {load.origin_city}</p>
                  {load.origin_address && (
                    <p><span className="font-medium">Address:</span> {load.origin_address}</p>
                  )}
                  <div className="mt-2 p-2 bg-green-50 rounded">
                    <p className="text-xs text-green-600">Journey Starting Point</p>
                  </div>
                </div>
              </div>
            </Popup>
          </Marker>
        )}

        {/* Current position marker with directional heading */}
        {telematics?.location?.latitude && 
         telematics?.location?.longitude && 
         (!showBreadcrumbs || selectedTimelineIndex === -1) && (
          <Marker
            position={[telematics.location.latitude, telematics.location.longitude]}
            icon={createVehicleIcon(
              telematics.heading || 0, 
              telematics.speed || 0, 
              telematics.source || 'app'
            )}
            zIndexOffset={600}
          >
            <Popup>
              <div className="p-3 min-w-[220px]">
                {/* Status Header */}
                <div className="flex items-center gap-2 mb-3">
                  {(telematics.speed || 0) < 2 ? (
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
                      <span className="text-gray-600 text-sm font-bold">P</span>
                    </div>
                  ) : (
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      telematics.source === 'samsara' ? 'bg-emerald-100' : 'bg-blue-100'
                    }`}>
                      <svg className={`w-4 h-4 ${telematics.source === 'samsara' ? 'text-emerald-600' : 'text-blue-600'}`} 
                           style={{ transform: `rotate(${telematics.heading || 0}deg)` }}
                           fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2L4 14h6v8l8-12h-6z"/>
                      </svg>
                    </div>
                  )}
                  <div>
                    <h3 className={`font-semibold ${
                      (telematics.speed || 0) < 2 ? 'text-gray-700' : 
                      telematics.source === 'samsara' ? 'text-emerald-600' : 'text-blue-600'
                    }`}>
                      {(telematics.speed || 0) < 2 ? 'Vehicle Parked' : 'In Transit'}
                    </h3>
                    <p className="text-xs text-gray-500">
                      {formatDateTime(telematics.timestamp)}
                    </p>
                  </div>
                </div>
                
                {/* Speed & Heading Info */}
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className={`p-2 rounded-lg ${(telematics.speed || 0) >= 55 ? 'bg-amber-50' : 'bg-gray-50'}`}>
                    <p className="text-[10px] uppercase tracking-wide text-gray-500 font-medium">Speed</p>
                    <p className={`text-lg font-bold ${
                      (telematics.speed || 0) >= 55 ? 'text-amber-600' : 
                      (telematics.speed || 0) < 2 ? 'text-gray-500' : 'text-gray-800'
                    }`}>
                      {Math.round(telematics.speed || 0)} <span className="text-xs font-normal">mph</span>
                    </p>
                  </div>
                  <div className="p-2 rounded-lg bg-gray-50">
                    <p className="text-[10px] uppercase tracking-wide text-gray-500 font-medium">Heading</p>
                    <p className="text-lg font-bold text-gray-800">
                      {telematics.heading != null ? `${Math.round(telematics.heading)}°` : '—'}
                    </p>
                  </div>
                </div>
                
                {/* Additional Info */}
                <div className="space-y-1.5 text-sm border-t border-gray-100 pt-2">
                  {telematics.vehicle?.name && (
                    <p className="flex justify-between">
                      <span className="text-gray-500">Vehicle</span>
                      <span className="font-medium text-gray-800">{telematics.vehicle.name}</span>
                    </p>
                  )}
                  {telematics.fuel_level != null && (
                    <p className="flex justify-between">
                      <span className="text-gray-500">Fuel Level</span>
                      <span className={`font-medium ${telematics.fuel_level < 20 ? 'text-red-600' : 'text-gray-800'}`}>
                        {Math.round(telematics.fuel_level)}%
                      </span>
                    </p>
                  )}
                  {telematics.engineState && (
                    <p className="flex justify-between">
                      <span className="text-gray-500">Engine</span>
                      <span className={`font-medium ${
                        telematics.engineState === 'On' ? 'text-green-600' : 'text-gray-500'
                      }`}>{telematics.engineState}</span>
                    </p>
                  )}
                </div>
                
                {/* Source Badge */}
                <div className={`mt-3 p-2 rounded-lg flex items-center gap-2 ${
                  telematics.source === 'samsara' ? 'bg-emerald-50' : 'bg-blue-50'
                }`}>
                  <span className="text-sm">{telematics.source === 'samsara' ? '📡' : '📱'}</span>
                  <p className={`text-xs font-medium ${
                    telematics.source === 'samsara' ? 'text-emerald-700' : 'text-blue-700'
                  }`}>
                    {telematics.source === 'samsara' ? 'Samsara ELD' : 'Driver App'} • Real-time
                  </p>
                </div>
              </div>
            </Popup>
          </Marker>
        )}

        {/* Destination marker */}
        {load.destination_latitude && load.destination_longitude && (
          <Marker
            position={[load.destination_latitude, load.destination_longitude]}
            icon={markers.destination}
            zIndexOffset={500}
          >
            <Popup>
              <div className="p-3">
                <h3 className="font-semibold text-red-600 mb-2">Delivery Location</h3>
                <div className="space-y-1 text-sm">
                  <p><span className="font-medium">City:</span> {load.destination_city}</p>
                  {load.destination_address && (
                    <p><span className="font-medium">Address:</span> {load.destination_address}</p>
                  )}
                  <div className="mt-2 p-2 bg-red-50 rounded">
                    <p className="text-xs text-red-600">Journey Destination</p>
                  </div>
                </div>
              </div>
            </Popup>
          </Marker>
        )}
      </MapContainer>

      {/* Layer Toggle Control */}
      <LayerToggleControl 
        activeLayer={activeLayer} 
        onLayerChange={setActiveLayer} 
      />

      {/* Journey Mode overlay with legend */}
      {showBreadcrumbs && trackingHistory.length > 0 && (
        <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm rounded-xl p-3 shadow-lg z-10 border border-gray-100">
          {selectedTimelineIndex >= 0 && trackingHistory[selectedTimelineIndex] ? (
            <>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 bg-violet-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-semibold text-gray-800">
                  Point {selectedTimelineIndex + 1} of {trackingHistory.length}
                </span>
              </div>
              <p className="text-xs text-gray-600 mb-3">
                {formatDateTime(trackingHistory[selectedTimelineIndex].created_at)}
              </p>
            </>
          ) : (
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm font-semibold text-gray-800">Journey Mode</span>
            </div>
          )}
          
          {/* Legend */}
          <div className="border-t border-gray-100 pt-2 space-y-1.5">
            <div className="flex items-center gap-2">
              <svg width="14" height="14" viewBox="0 0 24 24" className="text-blue-500">
                <circle cx="12" cy="12" r="10" fill="rgba(59, 130, 246, 0.15)" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M12 6 L17 14 L12 11 L7 14 Z" fill="currentColor"/>
              </svg>
              <span className="text-[10px] text-gray-600">Completed</span>
            </div>
            <div className="flex items-center gap-2">
              <svg width="14" height="14" viewBox="0 0 24 24" className="text-gray-400">
                <circle cx="12" cy="12" r="10" fill="rgba(156, 163, 175, 0.1)" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M12 6 L17 14 L12 11 L7 14 Z" fill="currentColor"/>
              </svg>
              <span className="text-[10px] text-gray-600">Upcoming</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3.5 h-3.5 bg-gradient-to-br from-violet-400 to-violet-600 rounded-full border-2 border-white shadow-sm"></div>
              <span className="text-[10px] text-gray-600">Selected</span>
            </div>
          </div>
        </div>
      )}
      
      {/* Custom styles for arrow markers */}
      <style>{`
        .arrow-marker {
          background: transparent !important;
          border: none !important;
        }
        .selected-point-marker {
          background: transparent !important;
          border: none !important;
        }
        .vehicle-marker {
          background: transparent !important;
          border: none !important;
        }
        .leaflet-marker-icon.arrow-marker,
        .leaflet-marker-icon.selected-point-marker,
        .leaflet-marker-icon.vehicle-marker {
          background: transparent !important;
        }
      `}</style>
    </div>
  );
};

export default LoadTrackingMap;